/*
 * certificate-maker/render.js — the on-screen half, plus the ruler both halves use.
 *
 * ONE SVG USER UNIT IS ONE MILLIMETRE. The viewBox is the page in millimetres,
 * the element is sized in CSS pixels, and no code in this file ever converts
 * between the two — the browser does it. That is what makes a Playwright test
 * able to assert "the name sits 96 mm from the top" by reading the DOM.
 *
 * THE MEASURER IS THE IMPORTANT PART
 * ──────────────────────────────────
 * `measurer()` is what layout.js uses to decide line breaks and type sizes, and
 * it reads the SAME TTF files that pdf.js embeds — declared as @font-face by the
 * blade. Two consequences worth stating because both were bugs elsewhere:
 *
 *   · Ascent is MEASURED (fontBoundingBoxAscent), never assumed as a fraction
 *     of the em. Display faces differ by 20 % here, and a guessed ascent puts
 *     the baseline visibly off on paper while looking fine on screen.
 *   · Measuring before the webfont has loaded measures the FALLBACK, so line
 *     breaks jump under the user's hands a second later. Callers re-layout on
 *     `document.fonts.ready`; `fontsFor()` reports which faces are actually
 *     resolved so a test can prove it rather than hope.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertRender = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var NS = 'http://www.w3.org/2000/svg';
    /* Scripts our display faces do not cover fall through to these, in the same
     * order pdf-text-font.js would pick them, so the preview and the PDF agree
     * about which face renders a Korean word inside a Russian sentence. */
    var FALLBACK = '"CM Noto Sans", "Noto Sans KR", "Noto Sans JP", "Noto Sans SC", "IBM Plex Sans Arabic", Georgia, serif';

    function el(name, attrs) {
        var n = document.createElementNS(NS, name);
        for (var k in attrs) {
            if (Object.prototype.hasOwnProperty.call(attrs, k) && attrs[k] != null && attrs[k] !== '') {
                n.setAttribute(k, String(attrs[k]));
            }
        }
        return n;
    }

    /* ── measurement ─────────────────────────────────────────────────────── */

    /* Metrics are taken at 100 px per em and divided, which keeps the numbers
     * far away from sub-pixel rounding at the 4 mm type sizes a foot line uses. */
    var _ctx = null;
    function measurer(cssFor) {
        if (!_ctx) _ctx = document.createElement('canvas').getContext('2d');
        var cache = {};

        return function (text, style) {
            var fam = cssFor(style.font);
            var key = (style.bold ? 'b' : '') + (style.italic ? 'i' : '') + '|' + fam + '|' + text;
            var hit = cache[key];
            if (!hit) {
                _ctx.font = (style.italic ? 'italic ' : '') + (style.bold ? '700 ' : '400 ') + '100px ' + fam;
                var m = _ctx.measureText(String(text == null ? '' : text));
                hit = cache[key] = {
                    w: m.width / 100,
                    /* actualBoundingBox* describes THIS string's ink and jumps
                     * between "Иванов" and "Иванова"; fontBoundingBox* describes
                     * the FACE and is stable, which is what a baseline needs. */
                    asc: (m.fontBoundingBoxAscent || 80) / 100,
                    desc: (m.fontBoundingBoxDescent || 20) / 100
                };
            }
            var n = String(text || '').length;
            var ls = style.letterSpacing || 0;
            return {
                w: hit.w * style.size + (ls > 0 && n > 1 ? ls * (n - 1) : 0),
                asc: hit.asc * style.size,
                desc: hit.desc * style.size
            };
        };
    }

    /** Which of the requested families the browser has actually resolved. */
    function fontsReady(list) {
        if (!document.fonts || !document.fonts.check) return true;
        return list.every(function (css) { return document.fonts.check('16px "' + css + '"'); });
    }

    /* ── drawing ─────────────────────────────────────────────────────────── */

    /**
     * draw(host, result, opts) — replaces the contents of `host` (an <svg>) with
     * the primitive list. `opts.interactive` tags text/overlay nodes with
     * data-block so the editor can hit-test and drag them.
     */
    function draw(host, result, opts) {
        opts = opts || {};
        var page = result.page;
        while (host.firstChild) host.removeChild(host.firstChild);
        host.setAttribute('viewBox', '0 0 ' + page.w + ' ' + page.h);
        host.setAttribute('preserveAspectRatio', 'xMidYMid meet');

        /* A scan of a bought blank sits UNDER everything and is never exported —
         * it is a positioning aid, not artwork. See pdf.js: the overprint export
         * simply does not receive it. */
        if (opts.backdrop) {
            host.appendChild(el('image', {
                href: opts.backdrop, x: 0, y: 0, width: page.w, height: page.h,
                preserveAspectRatio: 'none', opacity: opts.backdropOpacity == null ? 0.45 : opts.backdropOpacity,
                'data-backdrop': '1'
            }));
        }

        result.prims.forEach(function (p) { host.appendChild(node(p, opts)); });

        /* Safe-area outline: where the frame's inset says text may live. Off by
         * default; the one control people reach for when overprinting. */
        if (opts.showSafe && result.content) {
            var c = result.content;
            host.appendChild(el('rect', {
                x: c.x, y: c.y, width: c.w, height: c.h, fill: 'none',
                stroke: '#3b82f6', 'stroke-width': 0.25, 'stroke-dasharray': '2 2',
                opacity: 0.75, 'data-safe': '1', 'pointer-events': 'none'
            }));
        }

        /* Hit targets for dragging, drawn last so they sit on top. Transparent
         * rectangles rather than the glyphs themselves: a 2 mm tall line of
         * script type is impossible to grab by its ink. */
        if (opts.interactive) {
            (result.blocks || []).forEach(function (b) {
                if (!b.id) return;
                /* Tracking is added to the padding, not to the width: our
                   canvas `measure()` and the engine's own SVG advance disagree
                   by a fraction of one tracking unit on a spaced-out title, and
                   the last letter of «Г Р А М О Т А» fell outside the grab area
                   by 3 px at 1440 (Chromium, 2026-08-23). Absorbing it in the
                   padding keeps one definition of width for all three
                   renderers, which is the property worth protecting. */
                var pad = 1.6 + (b.letterSpacing || 0) / 2;
                var x = b.anchor === 'middle' ? b.x - b.w / 2 : b.anchor === 'end' ? b.x - b.w : b.x;
                var r = el('rect', {
                    x: (b.overlay ? b.x : x) - pad, y: b.y - pad,
                    width: Math.max(6, b.w) + pad * 2, height: Math.max(4, b.h) + pad * 2,
                    fill: 'transparent', 'data-block': b.id, 'data-overlay': b.overlay ? '1' : '',
                    class: 'cm-hit'
                });
                host.appendChild(r);
            });
        }
        return host;
    }

    function node(p, opts) {
        if (p.type === 'rect') {
            return el('rect', {
                x: p.x, y: p.y, width: p.w, height: p.h,
                fill: p.fill || 'none', stroke: p.color || 'none',
                'stroke-width': p.sw || 0, opacity: p.opacity == null ? null : p.opacity
            });
        }
        if (p.type === 'circle') {
            return el('circle', {
                cx: p.cx, cy: p.cy, r: p.r, fill: p.fill || 'none',
                stroke: p.color || 'none', 'stroke-width': p.w || 0,
                opacity: p.opacity == null ? null : p.opacity
            });
        }
        if (p.type === 'path') {
            return el('path', {
                d: p.d, fill: p.fill || 'none', stroke: p.color || 'none',
                'stroke-width': p.w || 0, opacity: p.opacity == null ? null : p.opacity,
                'stroke-dasharray': p.dash ? p.dash.join(' ') : null,
                'stroke-linejoin': 'round', 'stroke-linecap': 'round'
            });
        }
        if (p.type === 'image') {
            var g = el('g', p.rot ? { transform: 'rotate(' + p.rot + ' ' + (p.x + p.w / 2) + ' ' + (p.y + p.h / 2) + ')' } : {});
            g.appendChild(el('image', {
                href: p.src, x: p.x, y: p.y, width: p.w, height: p.h,
                preserveAspectRatio: 'xMidYMid meet',
                opacity: p.opacity == null ? null : p.opacity
                /* No blend mode. A scan's white is turned into alpha at upload,
                   because pdf-lib cannot blend and a preview-only blend would
                   show a signature here and a white box on paper. */
            }));
            return g;
        }
        if (p.type === 'text') {
            /* Letter spacing is applied START-anchored with the x pre-shifted,
             * because CSS letter-spacing adds a gap after the LAST glyph too:
             * with text-anchor=middle that trailing gap pushes the whole line
             * half a space left of centre. pdf.js advances glyph by glyph and
             * lands in the same place only because of this compensation. */
            var ls = p.letterSpacing || 0;
            var a = {
                y: p.y, fill: p.color, 'font-family': (opts.cssFor || String)(p.font),
                'font-size': p.size, 'font-weight': p.bold ? 700 : 400,
                'font-style': p.italic ? 'italic' : null,
                opacity: p.opacity == null ? null : p.opacity,
                'xml:space': 'preserve'
            };
            if (ls > 0) {
                a['letter-spacing'] = ls;
                a['text-anchor'] = 'start';
                a.x = p.x - (p.w || 0) * (p.anchor === 'middle' ? 0.5 : p.anchor === 'end' ? 1 : 0);
            } else {
                a.x = p.x;
                a['text-anchor'] = p.anchor || 'middle';
            }
            /* Which block these glyphs belong to. Not used for hit-testing —
               `data-block` on the transparent rectangle does that — but it makes
               «is the grab area actually over the text?» a question a test can
               answer, and that question was answered wrong for months. */
            if (p.blockId) a['data-of'] = p.blockId;
            var t = el('text', a);
            t.textContent = p.s;
            return t;
        }
        return el('g', {});
    }

    /** Serialise the current SVG for a PNG export or a debug dump. */
    function toSvgString(host, page, cssFaces) {
        var clone = host.cloneNode(true);
        // Hit targets and helper overlays are editor furniture, not artwork.
        Array.prototype.slice.call(clone.querySelectorAll('[data-block],[data-safe],[data-backdrop]'))
            .forEach(function (n) { n.parentNode.removeChild(n); });
        var defs = cssFaces ? '<defs><style type="text/css">' + cssFaces + '</style></defs>' : '';
        var inner = clone.innerHTML;
        return '<svg xmlns="' + NS + '" xmlns:xlink="http://www.w3.org/1999/xlink" width="' + page.w + 'mm" height="' + page.h +
            'mm" viewBox="0 0 ' + page.w + ' ' + page.h + '">' + defs + inner + '</svg>';
    }

    /* ── PNG, straight onto a canvas ─────────────────────────────────────────
     * NOT by rasterising the SVG. An <img src="data:image/svg+xml…"> is loaded
     * as an isolated document with no network access, so @font-face never
     * resolves and every display face silently falls back to the system serif —
     * a PNG that does not match the preview or the PDF. Drawing the primitives
     * straight onto a 2D context uses the faces the DOCUMENT has already
     * loaded, so all three outputs agree.
     *
     * This is the third renderer over one primitive list, and the reason there
     * is no fourth idea of where the text goes. */
    function toCanvas(result, dpi, cssFor) {
        var page = result.page;
        var px = (dpi || 300) / 25.4;                  // pixels per millimetre
        var cv = document.createElement('canvas');
        cv.width = Math.round(page.w * px);
        cv.height = Math.round(page.h * px);
        var c = cv.getContext('2d');
        c.scale(px, px);
        c.lineJoin = 'round';
        c.lineCap = 'round';
        c.textBaseline = 'alphabetic';

        var pending = [];
        result.prims.forEach(function (p) {
            /* ⚠️ `rect` used to return BEFORE the alpha was set below, so a
               translucent rectangle printed solid in the PNG while the SVG and
               the PDF drew it correctly. Nothing had one until the scrim
               arrived — and a panel meant to sit at 72 % over a photograph
               would have exported as an opaque slab covering the picture. */
            c.save();
            if (p.opacity != null) c.globalAlpha = p.opacity;
            if (p.type === 'rect') {
                if (p.fill && p.fill !== 'none') { c.fillStyle = p.fill; c.fillRect(p.x, p.y, p.w, p.h); }
                if (p.color && p.sw) { c.strokeStyle = p.color; c.lineWidth = p.sw; c.strokeRect(p.x, p.y, p.w, p.h); }
                c.restore();
                return;
            }
            if (p.type === 'circle') {
                c.beginPath();
                c.arc(p.cx, p.cy, p.r, 0, Math.PI * 2);
                if (p.fill && p.fill !== 'none') { c.fillStyle = p.fill; c.fill(); }
                if (p.color && p.w) { c.strokeStyle = p.color; c.lineWidth = p.w; c.stroke(); }
            } else if (p.type === 'path') {
                var path = new Path2D(p.d);
                if (p.fill && p.fill !== 'none') { c.fillStyle = p.fill; c.fill(path); }
                if (p.color && p.w) {
                    c.strokeStyle = p.color;
                    c.lineWidth = p.w;
                    if (p.dash) c.setLineDash(p.dash);
                    c.stroke(path);
                    c.setLineDash([]);
                }
            } else if (p.type === 'text') {
                c.font = (p.italic ? 'italic ' : '') + (p.bold ? '700 ' : '400 ') + p.size + 'px ' + cssFor(p.font);
                c.fillStyle = p.color;
                var ls = p.letterSpacing || 0;
                /* ⚠️ THIS CANVAS IS DETACHED, so its direction is the initial
                 * `ltr` — not the page's. Measured 2026-08-24 on the ARABIC page:
                 * the context still reported a left-to-right paragraph, and the
                 * two directions draw a mixed line in opposite orders. The PDF
                 * takes the direction from the TEXT; ask the same question here,
                 * or the PNG and the PDF of one certificate disagree. */
                var rtlPara = !!(window.PdfTextFont && window.PdfTextFont.baseIsRtl
                    && window.PdfTextFont.baseIsRtl(p.s));
                try { c.direction = rtlPara ? 'rtl' : 'ltr'; } catch (e) {}
                if (!ls) {
                    c.textAlign = p.anchor === 'start' ? 'left' : p.anchor === 'end' ? 'right' : 'center';
                    c.fillText(p.s, p.x, p.y);
                } else if (rtlPara || typeof c.letterSpacing === 'string') {
                    /* ⛔ NEVER STEP A RIGHT-TO-LEFT LINE GLYPH BY GLYPH. Arabic
                     * letters take their shape from their neighbours, so one
                     * fillText per character asks the shaper to shape each letter
                     * alone and every one comes out isolated. Measured on
                     * «shahada taqdir»: 295 px of ink against 215 for the same
                     * string drawn whole — 37% wider, and the word falls apart.
                     * The PDF stopped doing this on 2026-08-23; the note that used
                     * to sit here still claimed the two paths matched. */
                    var prev = c.letterSpacing;
                    try { c.letterSpacing = ls.toFixed(3) + 'px'; } catch (e) {}
                    /* With letterSpacing set the measured width carries a gap after
                       the LAST glyph too, and that gap is advance, not ink. */
                    var inkLs = Math.max(0, c.measureText(p.s).width - ls);
                    c.textAlign = 'left';
                    c.fillText(p.s, p.x - (p.anchor === 'middle' ? inkLs / 2 : p.anchor === 'end' ? inkLs : 0), p.y);
                    try { c.letterSpacing = prev || '0px'; } catch (e) {}
                } else {
                    /* Older engines have no letterSpacing. Stepping the glyphs is
                       safe for a left-to-right line, where nothing joins: the ink
                       is the glyphs plus n-1 gaps, and the start x follows. */
                    c.textAlign = 'left';
                    var chars = Array.from(p.s);
                    var ink = 0;
                    chars.forEach(function (ch) { ink += c.measureText(ch).width; });
                    ink += ls * Math.max(0, chars.length - 1);
                    var x = p.x - (p.anchor === 'middle' ? ink / 2 : p.anchor === 'end' ? ink : 0);
                    chars.forEach(function (ch) {
                        c.fillText(ch, x, p.y);
                        x += c.measureText(ch).width + ls;
                    });
                }
            } else if (p.type === 'image' && p.src) {
                pending.push(new Promise(function (res) {
                    var img = new Image();
                    img.onload = function () {
                        var ar = img.naturalWidth / img.naturalHeight;
                        var w = p.w, h = p.h;
                        if (w / h > ar) w = h * ar; else h = w / ar;
                        var cx = p.x + p.w / 2, cy = p.y + p.h / 2;
                        c.save();
                        if (p.opacity != null) c.globalAlpha = p.opacity;
                        if (p.rot) { c.translate(cx, cy); c.rotate(p.rot * Math.PI / 180); c.translate(-cx, -cy); }
                        c.drawImage(img, cx - w / 2, cy - h / 2, w, h);
                        c.restore();
                        res();
                    };
                    img.onerror = function () { res(); };
                    img.src = p.src;
                }));
            }
            c.restore();
        });

        return Promise.all(pending).then(function () { return cv; });
    }

    return { measurer: measurer, draw: draw, toCanvas: toCanvas, toSvgString: toSvgString, fontsReady: fontsReady, FALLBACK: FALLBACK, _node: node };
});
